import i18n, { type InitOptions } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend, { type HttpBackendOptions } from "i18next-http-backend";
import { initReactI18next } from "react-i18next";
import translationBE from "@/assets/locales/be/translations.json";
import translationBG from "@/assets/locales/bg/translations.json";
import translationBN from "@/assets/locales/bn/translations.json";
import translationBS from "@/assets/locales/bs/translations.json";
import translationCA from "@/assets/locales/ca/translations.json";
import translationCS from "@/assets/locales/cs/translations.json";
import translationDA from "@/assets/locales/da/translations.json";
import translationDE from "@/assets/locales/de/translations.json";
import translationEL from "@/assets/locales/el/translations.json";
import translationEN from "@/assets/locales/en/translations.json";
import translationES from "@/assets/locales/es/translations.json";
import translationET from "@/assets/locales/et/translations.json";
import translationFA from "@/assets/locales/fa/translations.json";
import translationFI from "@/assets/locales/fi/translations.json";
import translationFIL from "@/assets/locales/fil/translations.json";
import translationFR from "@/assets/locales/fr/translations.json";
import translationGL from "@/assets/locales/gl/translations.json";
import translationGU from "@/assets/locales/gu/translations.json";
import translationHI from "@/assets/locales/hi/translations.json";
import translationHU from "@/assets/locales/hu/translations.json";
import translationID from "@/assets/locales/id/translations.json";
import translationIT from "@/assets/locales/it/translations.json";
import translationJA from "@/assets/locales/ja/translations.json";
import translationKK from "@/assets/locales/kk/translations.json";
import translationKN from "@/assets/locales/kn/translations.json";
import translationLT from "@/assets/locales/lt/translations.json";
import translationLV from "@/assets/locales/lv/translations.json";
import translationMK from "@/assets/locales/mk/translations.json";
import translationML from "@/assets/locales/ml/translations.json";
import translationMR from "@/assets/locales/mr/translations.json";
import translationMS from "@/assets/locales/ms/translations.json";
import translationNE from "@/assets/locales/ne/translations.json";
import translationNL from "@/assets/locales/nl/translations.json";
import translationNO from "@/assets/locales/no/translations.json";
import translationOR from "@/assets/locales/or/translations.json";
import translationPL from "@/assets/locales/pl/translations.json";
import translationPT from "@/assets/locales/pt/translations.json";
import translationRO from "@/assets/locales/ro/translations.json";
import translationRU from "@/assets/locales/ru/translations.json";
import translationSK from "@/assets/locales/sk/translations.json";
import translationSL from "@/assets/locales/sl/translations.json";
import translationSR from "@/assets/locales/sr/translations.json";
import translationSV from "@/assets/locales/sv/translations.json";
import translationTA from "@/assets/locales/ta/translations.json";
import translationTE from "@/assets/locales/te/translations.json";
import translationTR from "@/assets/locales/tr/translations.json";
import translationUK from "@/assets/locales/uk/translations.json";
import translationVI from "@/assets/locales/vi/translations.json";
import translationZH from "@/assets/locales/zh/translations.json";

import { isProduction } from "./constants";

export const defaultNS = "translations";
export const resources = {
  en: { translations: translationEN },
  es: { translations: translationES },
  ja: { translations: translationJA },
  pt: { translations: translationPT },
  fr: { translations: translationFR },
  it: { translations: translationIT },
  zh: { translations: translationZH },
  ru: { translations: translationRU },
  uk: { translations: translationUK },
  hi: { translations: translationHI },
  be: { translations: translationBE },
  bs: { translations: translationBS },
  da: { translations: translationDA },
  et: { translations: translationET },
  gl: { translations: translationGL },
  id: { translations: translationID },
  lt: { translations: translationLT },
  ml: { translations: translationML },
  ne: { translations: translationNE },
  or: { translations: translationOR },
  sk: { translations: translationSK },
  sv: { translations: translationSV },
  tr: { translations: translationTR },
  bg: { translations: translationBG },
  ca: { translations: translationCA },
  de: { translations: translationDE },
  fa: { translations: translationFA },
  gu: { translations: translationGU },
  kk: { translations: translationKK },
  lv: { translations: translationLV },
  mr: { translations: translationMR },
  nl: { translations: translationNL },
  pl: { translations: translationPL },
  sl: { translations: translationSL },
  ta: { translations: translationTA },
  vi: { translations: translationVI },
  bn: { translations: translationBN },
  cs: { translations: translationCS },
  el: { translations: translationEL },
  fi: { translations: translationFI },
  hu: { translations: translationHU },
  kn: { translations: translationKN },
  mk: { translations: translationMK },
  ms: { translations: translationMS },
  no: { translations: translationNO },
  ro: { translations: translationRO },
  sr: { translations: translationSR },
  te: { translations: translationTE },
  fil: { translations: translationFIL },
} as const;

const detector = new LanguageDetector(null, {
  caches: [],
});

// Before anyone picks a language (sign-in, sign-up, the code screen), follow
// the browser, but only for the interface languages offered in the app
// (fully translated); any other browser language keeps English. The language
// a person picks is saved in this browser and always wins.
// Same list as interfaceLanguageOptions (constants.ts; a test keeps them
// equal), kept here so starting i18n does not depend on that module.
export const OFFERED_INTERFACE_LANGUAGES: readonly string[] = [
  "en",
  "es",
  "pt",
  "fr",
  "ja",
  "zh",
];
export const INTERFACE_NAVIGATOR_DETECTOR = "interfaceNavigator";
export const interfaceLanguageFromNavigator = (
  languages: readonly string[] | undefined,
): string | undefined => {
  const offered = new Set(OFFERED_INTERFACE_LANGUAGES);
  for (const tag of languages ?? []) {
    const primary = tag.split("-")[0]?.toLowerCase();
    if (primary && offered.has(primary)) return primary;
  }
  return undefined;
};
detector.addDetector({
  name: INTERFACE_NAVIGATOR_DETECTOR,
  lookup: () =>
    typeof navigator === "undefined"
      ? undefined
      : interfaceLanguageFromNavigator(
          navigator.languages?.length
            ? navigator.languages
            : navigator.language
              ? [navigator.language]
              : [],
        ),
});

const i18nOptions: InitOptions = {
  defaultNS,
  ns: [defaultNS],
  debug: !isProduction,
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
  supportedLngs: Object.keys(resources),
  resources: resources,
  detection: {
    order: ["localStorage", INTERFACE_NAVIGATOR_DETECTOR],
    caches: ["localStorage"],
  },
};

void i18n
  .use(initReactI18next)
  .use(detector)
  .use(Backend)
  .init<HttpBackendOptions>(i18nOptions);

export default i18n;
